"use client";

import React, { createContext, useContext, ReactNode } from "react";
import { useDeviceDetection } from "@/app/_module/hooks/useDeviceDetection";

// Types from the hook
export type DeviceType = "mobile" | "tablet" | "laptop" | "desktop";
export type Orientation = "portrait" | "landscape";

interface DeviceBreakpoints {
  mobile: { maxWidth: number };
  tablet: { minWidth: number; maxWidth: number };
  laptop: { minWidth: number; maxWidth: number };
  desktop: { minWidth: number };
}

interface DeviceContextValue {
  type: DeviceType;
  orientation: Orientation;
  isTouchDevice: boolean;
  screenWidth: number;
  screenHeight: number;
  isIOS: boolean;
  isAndroid: boolean;
  isIPad: boolean;
  isIPhone: boolean;
  isMobileDevice: boolean;
  isTabletDevice: boolean;
  pixelRatio: number;
  isMobile: boolean;
  isTablet: boolean;
  isLaptop: boolean;
  isDesktop: boolean;
  isMobileOrTablet: boolean;
  isLaptopOrDesktop: boolean;
  breakpoints: DeviceBreakpoints;
  // Helper functions for responsive sizing
  getResponsiveClass: (options: ResponsiveClassOptions) => string;
  getResponsiveMaxWidth: (size?: ResponsiveSize) => string;
}

export type ResponsiveSize = "sm" | "md" | "lg" | "xl" | "2xl" | "3xl" | "full";

interface ResponsiveClassOptions {
  mobile: string;
  tablet?: string;
  laptop?: string;
  desktop?: string;
}

// Size mappings for different devices (used for dialogs, containers, etc.)
const SIZE_MAP = {
  sm: {
    mobile: "max-w-xs",      // 320px
    tablet: "max-w-sm",      // 384px
    laptop: "max-w-sm",      // 384px
    desktop: "max-w-md",     // 448px
  },
  md: {
    mobile: "max-w-sm",      // 384px
    tablet: "max-w-md",      // 448px
    laptop: "max-w-md",      // 448px
    desktop: "max-w-lg",     // 512px
  },
  lg: {
    mobile: "max-w-sm",      // 384px
    tablet: "max-w-md",      // 448px
    laptop: "max-w-lg",      // 512px
    desktop: "max-w-xl",     // 576px
  },
  xl: {
    mobile: "max-w-md",      // 448px
    tablet: "max-w-lg",      // 512px
    laptop: "max-w-xl",      // 576px
    desktop: "max-w-2xl",    // 672px
  },
  "2xl": {
    mobile: "max-w-md",      // 448px
    tablet: "max-w-xl",      // 576px
    laptop: "max-w-2xl",     // 672px
    desktop: "max-w-3xl",    // 768px
  },
  "3xl": {
    mobile: "max-w-lg",      // 512px
    tablet: "max-w-2xl",     // 672px
    laptop: "max-w-3xl",     // 768px
    desktop: "max-w-4xl",    // 896px
  },
  full: {
    mobile: "max-w-[95vw]",
    tablet: "max-w-[90vw]",
    laptop: "max-w-[85vw]",
    desktop: "max-w-[80vw]",
  },
} as const;

// Create the context with a default value
const DeviceDetectionContext = createContext<DeviceContextValue | null>(null);

// Provider component
interface DeviceDetectionProviderProps {
  children: ReactNode;
}

export function DeviceDetectionProvider({ children }: DeviceDetectionProviderProps) {
  const deviceInfo = useDeviceDetection();

  // Helper function to get a class based on current device
  const getResponsiveClass = (options: ResponsiveClassOptions): string => {
    if (deviceInfo.isMobile) return options.mobile;
    if (deviceInfo.isTablet) return options.tablet ?? options.mobile;
    if (deviceInfo.isLaptop) return options.laptop ?? options.tablet ?? options.mobile;
    if (deviceInfo.isDesktop) return options.desktop ?? options.laptop ?? options.tablet ?? options.mobile;
    return options.mobile; // fallback
  };

  // Helper function to get responsive max-width class
  const getResponsiveMaxWidth = (size: ResponsiveSize = "lg"): string => {
    const sizes = SIZE_MAP[size];
    if (deviceInfo.isMobile) return sizes.mobile;
    if (deviceInfo.isTablet) return sizes.tablet;
    if (deviceInfo.isLaptop) return sizes.laptop;
    if (deviceInfo.isDesktop) return sizes.desktop;
    return sizes.laptop; // fallback
  };

  const value: DeviceContextValue = {
    ...deviceInfo,
    getResponsiveClass,
    getResponsiveMaxWidth,
  };

  return (
    <DeviceDetectionContext.Provider value={value}>
      {children}
    </DeviceDetectionContext.Provider>
  );
}

// Hook to use the device detection context
export function useDeviceContext(): DeviceContextValue {
  const context = useContext(DeviceDetectionContext);

  // If no context (not wrapped in provider), fall back to using the hook directly
  // This ensures backward compatibility
  if (!context) {
    // eslint-disable-next-line react-hooks/rules-of-hooks
    const deviceInfo = useDeviceDetection();

    const getResponsiveClass = (options: ResponsiveClassOptions): string => {
      if (deviceInfo.isMobile) return options.mobile;
      if (deviceInfo.isTablet) return options.tablet ?? options.mobile;
      if (deviceInfo.isLaptop) return options.laptop ?? options.tablet ?? options.mobile;
      if (deviceInfo.isDesktop) return options.desktop ?? options.laptop ?? options.tablet ?? options.mobile;
      return options.mobile;
    };

    const getResponsiveMaxWidth = (size: ResponsiveSize = "lg"): string => {
      const sizes = SIZE_MAP[size];
      if (deviceInfo.isMobile) return sizes.mobile;
      if (deviceInfo.isTablet) return sizes.tablet;
      if (deviceInfo.isLaptop) return sizes.laptop;
      if (deviceInfo.isDesktop) return sizes.desktop;
      return sizes.laptop;
    };

    return {
      ...deviceInfo,
      getResponsiveClass,
      getResponsiveMaxWidth,
    };
  }

  return context;
}

// Export the size map for external use
export { SIZE_MAP };
